import { PLAYER } from "../../../common/constants.ts";
import { finances, freeAgents, league, player, realRosters } from "../index.ts";
import genPlayersWithoutSaving from "./genPlayersWithoutSaving.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { getNumPlayersTradedAwayNormalizedAll } from "../player/getNumPlayersTradedAwayNormalized.ts";
import { randInt } from "../../../common/random.ts";
import { DRAFT_ELIGIBLE_COUNTRIES } from "../../../common/constants.baseball.ts";

const goesThroughDraft = (bornLoc: string | undefined) =>
	DRAFT_ELIGIBLE_COUNTRIES.has(helpers.getCountry(bornLoc));

// Real international amateurs sign at 16, not at draft age. Weights are roughly the shape of a
// real signing class: the overwhelming majority are 16 or 17, with a thin tail of older players.
const SIGNING_AGE_WEIGHTS: [age: number, weight: number][] = [
	[16, 45],
	[17, 30],
	[18, 12],
	[19, 6],
	[20, 3],
	[21, 2],
	[22, 1],
	[23, 1],
];

const randomSigningAge = () => {
	const total = SIGNING_AGE_WEIGHTS.reduce(
		(sum, [, weight]) => sum + weight,
		0,
	);
	let roll = Math.random() * total;
	for (const [age, weight] of SIGNING_AGE_WEIGHTS) {
		roll -= weight;
		if (roll <= 0) {
			return age;
		}
	}
	return SIGNING_AGE_WEIGHTS[0]![0];
};

// Countries with a professional league players get posted from. Japan sends far more than Korea.
const POSTING_COUNTRIES = ["Japan", "Japan", "Japan", "South Korea"];

// Established professionals posted from Japan or Korea each season: one or two arrive ready to play,
// and a real star (Ichiro, Darvish, Yamamoto) at most once every three seasons, never guaranteed.
// genPlayers runs twice a season (current class and the one three years ahead), so the season of the
// last posting is stored to keep it to one round a season.
const POSTED_PER_SEASON = [1, 2] as const;
const POSTED_OVR = [60, 74] as const;
const STAR_OVR = [75, 82] as const;
const STAR_EVERY = 3;
const STAR_CHANCE = 0.5;

// Babe Ruth (1914) and Shohei Ohtani (2018) turned pro 104 years apart, so the next two-way player
// signs in 2122. He is an ordinary 16 year old prospect on both sides who develops a little faster
// (see player.develop).
const TWO_WAY_FIRST = 2018;
const TWO_WAY_EVERY = 104;

// These are only set once something has been generated, so older leagues don't have them
const lastSeason = (key: "postingSeason" | "postingStarSeason" | "twoWaySeason") =>
	Object.hasOwn(g, key) ? g.get(key) : undefined;

// How many candidates to draw before keeping the best. A posted player already had a career
// abroad, so he should arrive as one of the better players in the league, not as a lottery ticket.
const POSTING_CANDIDATES = 30;

// The top-up loop below keeps generating until the draft class is full, and everyone diverted
// abroad on the way is kept. If the league's birth countries ever skew heavily foreign, that
// turns into thousands of teenagers dumped into free agency in a single year. Capped at roughly
// one class worth, which is already far more than any real signing period.
const MAX_INTERNATIONALS_PER_CLASS = 120;

const HITTING = ["con", "hpw", "eye", "spd"];
const FIELDING = ["gnd", "fly", "thr", "cat"];

// The ratings that make him good at his own position. Raising every rating (catching on a second
// baseman, fly balls on a shortstop) made every posted player good everywhere.
const POSITION_RATINGS: Record<string, string[]> = {
	SP: ["ppw", "ctl", "mov", "endu"],
	RP: ["ppw", "ctl", "mov"],
	C: [...HITTING, "cat", "thr"],
	"1B": [...HITTING, "gnd", "thr"],
	"2B": [...HITTING, "gnd", "thr"],
	"3B": [...HITTING, "gnd", "thr"],
	SS: [...HITTING, "gnd", "thr"],
	LF: [...HITTING, "fly", "thr"],
	CF: [...HITTING, "fly", "thr"],
	RF: [...HITTING, "fly", "thr"],
};

// Shift his position's ratings by the same amount until his ovr reaches the target, keeping his profile
export const makeEstablished = async (
	p: Awaited<ReturnType<typeof player.generate>>,
	target: number,
) => {
	const r = p.ratings.at(-1)!;
	const keys = POSITION_RATINGS[r.pos] ?? HITTING;
	const base = Object.fromEntries(keys.map((k) => [k, (r as any)[k] as number]));
	const at = (shift: number) => {
		const test: any = { ...r };
		for (const k of keys) test[k] = helpers.bound(Math.round(base[k]! + shift), 0, 100);
		return player.ovr(test, r.pos);
	};
	let lo = -60;
	let hi = 60;
	for (let i = 0; i < 20; i++) {
		const mid = (lo + hi) / 2;
		if (at(mid) < target) lo = mid;
		else hi = mid;
	}
	// Rounding can jump past the target; stay just under it rather than over (a 74 is not a star)
	const shift = at(hi) <= target ? hi : lo;
	for (const k of keys) (r as any)[k] = helpers.bound(Math.round(base[k]! + shift), 0, 100);
	await player.develop(p, 0);
};

// Ordinary prospects on both sides: a starter's arm with a position player's bat and glove
export const genTwoWay = async (scoutingLevel: number) => {
	const generate = async () =>
		player.generate(
			PLAYER.UNDRAFTED,
			16,
			g.get("season"),
			false,
			scoutingLevel,
			await player.name("Japan"),
		);
	let pitcher = await generate();
	for (let i = 0; i < 1000 && pitcher.ratings[0].pos !== "SP"; i++) {
		pitcher = await generate();
	}
	let hitter = await generate();
	for (
		let i = 0;
		i < 1000 && (hitter.ratings[0].pos === "SP" || hitter.ratings[0].pos === "RP");
		i++
	) {
		hitter = await generate();
	}
	for (const key of [...HITTING, ...FIELDING]) {
		(pitcher.ratings[0] as any)[key] = (hitter.ratings[0] as any)[key];
	}
	pitcher.twoWay = true;
	await player.develop(pitcher, 0);
	return pitcher;
};

const genPlayers = async (
	draftYear: number,
	scoutingLevel?: number,
	forceScrubs?: boolean,
) => {
	// If scoutingLevel is not supplied, have to hit the DB to get it
	if (scoutingLevel === undefined) {
		scoutingLevel = await finances.getLevelLastThree("scouting", {
			tid: g.get("userTid"),
		});
	}

	const allDraftProspects = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.UNDRAFTED,
	);

	const existingPlayers = allDraftProspects.filter(
		(p) => p.draft.year === draftYear,
	);

	// Trigger randomDebutsForever?
	if (g.get("randomDebutsForever") !== undefined) {
		// Trigger condition - draftYear has no real players in it, or the year after draftYear has no real players in it
		const currentRealPlayers = existingPlayers.filter((p) => p.real).length;
		const nextRealPlayers = allDraftProspects.filter(
			(p) => p.real && p.draft.year === draftYear + 1,
		).length;
		if (currentRealPlayers === 0 || nextRealPlayers === 0) {
			await realRosters.updateRandomDebutsForever(
				draftYear,
				currentRealPlayers,
			);
			return;
		}
	}

	// Baseball only: players born outside the US/Canada/Puerto Rico never enter the amateur
	// draft, they sign as international free agents instead. Diverting them shrinks the draft
	// class, so generate again (passing the prospects kept so far as "existing") until the
	// class reaches its normal size. genPlayersWithoutSaving returns nothing once the target
	// is met, which ends the loop.
	type Generated = Awaited<ReturnType<typeof genPlayersWithoutSaving>>;
	const draftProspects: Generated = [];
	const internationals: Generated = [];

	let batch = await genPlayersWithoutSaving(
		draftYear,
		scoutingLevel,
		existingPlayers,
		forceScrubs,
	);

	for (let i = 0; i < 10; i++) {
		for (const p of batch) {
			if (isSport("baseball") && !goesThroughDraft(p.born.loc)) {
				internationals.push(p);
			} else {
				draftProspects.push(p);
			}
		}

		if (
			!isSport("baseball") ||
			internationals.length === 0 ||
			internationals.length >= MAX_INTERNATIONALS_PER_CLASS
		) {
			break;
		}

		batch = await genPlayersWithoutSaving(
			draftYear,
			scoutingLevel,
			[...existingPlayers, ...draftProspects],
			forceScrubs,
		);

		if (batch.length === 0) {
			break;
		}
	}

	// The batch above was generated at draft age. Rebuild each international at the age he would
	// really have signed, keeping his country. They enter FA NOW, even if this call fills
	// a draft class three years ahead: both draft year and ratings season must be current.
	// Rebuilding rather than just editing born.year
	// matters: ratings are generated for a given age, so a 16 year old carrying a 20 year old's
	// ratings would be a phenom every single year.
	if (isSport("baseball") && internationals.length > 0) {
		for (const [i, p] of internationals.entries()) {
			const country = helpers.getCountry(p.born.loc);
			const nameInfo = await player.name(country);
			const young = player.generate(
				PLAYER.UNDRAFTED,
				randomSigningAge(),
				g.get("season"),
				false,
				scoutingLevel,
				nameInfo,
			);
			await player.develop(young, 0);
			internationals[i] = young;
		}
	}

	// Established professionals posted from Japan or Korea. Unlike the teenagers above they arrive
	// ready to play, so each is drawn as the best of many candidates at an age near his peak.
	const season = g.get("season");
	const posted: typeof internationals = [];
	if (
		isSport("baseball") &&
		!forceScrubs &&
		lastSeason("postingSeason") !== season
	) {
		const star =
			season - (lastSeason("postingStarSeason") ?? -Infinity) >= STAR_EVERY &&
			Math.random() < STAR_CHANCE;
		const count = randInt(...POSTED_PER_SEASON);
		for (let k = 0; k < count; k++) {
			const country =
				POSTING_COUNTRIES[randInt(0, POSTING_COUNTRIES.length - 1)]!;
			const age = randInt(24, 28);
			let best: (typeof internationals)[number] | undefined;
			for (let i = 0; i < POSTING_CANDIDATES; i++) {
				const nameInfo = await player.name(country);
				const candidate = player.generate(
					PLAYER.UNDRAFTED,
					age,
					season,
					false,
					scoutingLevel,
					nameInfo,
				);
				await player.develop(candidate, 0);
				if (!best || candidate.ratings.at(-1)!.ovr > best.ratings.at(-1)!.ovr) {
					best = candidate;
				}
			}
			if (best) {
				await makeEstablished(
					best,
					k === 0 && star ? randInt(...STAR_OVR) : randInt(...POSTED_OVR),
				);
				best.posted = season;
				posted.push(best);
				internationals.push(best);
			}
		}
		await league.setGameAttributes({
			postingSeason: season,
			...(star ? { postingStarSeason: season } : {}),
		});
	}

	if (
		isSport("baseball") &&
		!forceScrubs &&
		season > TWO_WAY_FIRST &&
		(season - TWO_WAY_FIRST) % TWO_WAY_EVERY === 0 &&
		lastSeason("twoWaySeason") !== season
	) {
		internationals.push(await genTwoWay(scoutingLevel));
		await league.setGameAttributes({ twoWaySeason: season });
	}

	const players = [...draftProspects, ...internationals];

	let numPlayersTradedAwayNormalized;
	const numInternational = internationals.length;
	if (numInternational > 0) {
		numPlayersTradedAwayNormalized =
			await getNumPlayersTradedAwayNormalizedAll();
		for (const p of internationals) {
			player.addToFreeAgents(p, numPlayersTradedAwayNormalized);
		}
	}

	for (const p of players) {
		await idb.cache.players.add(p);

		// idb.cache.players.add will create the "pid" property, transforming PlayerWithoutKey to Player
		// @ts-expect-error
		await player.addRelatives(p);

		await player.updateValues(p);
	}

	if (numInternational > 0) {
		// Free agents need contract demands, which draft prospects do not have
		await freeAgents.normalizeContractDemands({
			type: "freeAgentsOnly",
		});
	}

	// A posted player would otherwise be lost among a hundred teenagers in the free agent list,
	// which defeats the point of him arriving ready to play.
	for (const p of posted) {
		if (p.pid === undefined) {
			continue;
		}
		await logEvent({
			type: "posting",
			text: `<a href="${helpers.leagueUrl(["player", p.pid])}">${
				p.firstName
			} ${p.lastName}</a>, a ${
				g.get("season") - p.born.year
			} year old ${p.ratings.at(-1)!.pos}, was posted from ${
				p.born.loc
			} and is now a free agent.`,
			showNotification: true,
			pids: [p.pid],
			score: 20,
		});
	}

	// Easter eggs!
	if (isSport("basketball") && !forceScrubs && g.get("easterEggPlayers")) {
		if (Math.random() < 1 / 100000) {
			const p = player.generate(
				PLAYER.UNDRAFTED,
				19,
				draftYear,
				false,
				scoutingLevel,
			);
			p.born.year = draftYear - 48;
			p.born.loc = "Los Angeles, CA";
			p.college = "Washington State University";
			p.firstName = "LaVar";
			p.hgt = 78;
			p.imgURL = "/img/lavar.jpg";
			p.lastName = "Ball";
			p.weight = 250;
			Object.assign(p.ratings[0], {
				hgt: 43,
				stre: 80,
				spd: 80,
				jmp: 80,
				endu: 80,
				ins: 80,
				dnk: 80,
				ft: 80,
				fg: 80,
				tp: 80,
				oiq: 80,
				diq: 80,
				drb: 80,
				pss: 80,
				reb: 80,
			});
			await player.develop(p, 0);
			await player.updateValues(p);
			const pid = await idb.cache.players.add(p);

			if (typeof pid === "number") {
				await logEvent({
					type: "playerFeat",
					text: `<a href="${helpers.leagueUrl(["player", pid])}">${
						p.firstName
					} ${
						p.lastName
					}</a> got sick of the haters and decided to show the world how a big baller plays.`,
					showNotification: false,
					pids: [pid],
					tids: [g.get("userTid")],
				});
			}
			// eslint-disable-next-line no-dupe-else-if
		} else if (Math.random() < 1 / 100000) {
			const p = player.generate(
				PLAYER.UNDRAFTED,
				19,
				draftYear,
				false,
				scoutingLevel,
			);
			p.born.year = draftYear - 47;
			p.born.loc = "Honolulu, HI";
			p.college = "Columbia University";
			p.firstName = "Barack";
			p.hgt = 73;
			p.imgURL = "/img/obama.jpg";
			p.lastName = "Obama";
			p.weight = 175;
			Object.assign(p.ratings[0], {
				hgt: 20,
				stre: 80,
				spd: 80,
				jmp: 80,
				endu: 80,
				ins: 90,
				dnk: 80,
				ft: 80,
				fg: 80,
				tp: 40,
				oiq: 80,
				diq: 80,
				drb: 90,
				pss: 80,
				reb: 100,
			});
			await player.develop(p, 0);
			await player.updateValues(p);
			p.ratings[0].skills = ["Dp"];
			const pid = await idb.cache.players.add(p);

			if (typeof pid === "number") {
				await logEvent({
					type: "playerFeat",
					text: `<a href="${helpers.leagueUrl(["player", pid])}">${
						p.firstName
					} ${
						p.lastName
					}</a> decided to bring hope and change to Basketball GM.`,
					showNotification: false,
					pids: [pid],
					tids: [g.get("userTid")],
				});
			}
			// eslint-disable-next-line no-dupe-else-if
		} else if (Math.random() < 1 / 100000) {
			const p = player.generate(
				PLAYER.UNDRAFTED,
				19,
				draftYear,
				false,
				scoutingLevel,
			);
			p.born.year = draftYear - 70;
			p.born.loc = "Queens, NY";
			p.college = "Wharton";
			p.firstName = "Donald";
			p.hgt = 75;
			p.imgURL = "/img/trump.jpg";
			p.lastName = "Trump";
			p.weight = 240;
			Object.assign(p.ratings[0], {
				hgt: 40,
				stre: 80,
				spd: 80,
				jmp: 80,
				endu: 80,
				ins: 80,
				dnk: 80,
				ft: 80,
				fg: 80,
				tp: 80,
				oiq: 80,
				diq: 100,
				drb: 80,
				pss: 0,
				reb: 80,
			});
			await player.develop(p, 0);
			await player.updateValues(p);
			p.ratings[0].skills = ["Dp"];
			const pid = await idb.cache.players.add(p);

			if (typeof pid === "number") {
				await logEvent({
					type: "playerFeat",
					text: `<a href="${helpers.leagueUrl(["player", pid])}">${
						p.firstName
					} ${p.lastName}</a> decided to Make Basketball GM Great Again.`,
					showNotification: false,
					pids: [pid],
					tids: [g.get("userTid")],
				});
			}
		}
	}
};

export default genPlayers;
